import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { collection, doc, DocumentSnapshot, Firestore, getDoc, getDocs, setDoc, writeBatch } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';
import { Case } from '../../core/models';
import { PathConstants } from '../../db.paths';

@Injectable({ providedIn: 'root' })
export class CaseService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private functions = inject(Functions);
  private injector = inject(Injector);
  private cases$ = new BehaviorSubject<Case | null>(null);
  caseDetails$ = this.cases$.asObservable();

  get currentCaseDetails(): Case | null {
    return this.cases$.value;
  }

  constructor() { }

  clearCaseDetails(): void {
    this.cases$.next(null);
  }

  // Removed getCases() as it was using mock data

  getCaseByPatientKey(patientKey: string): Observable<Case | null> {
    return this.authService.getLoggedInUser().pipe(
      switchMap(user => {
        if (!user?.uid) {
          console.error('User not logged in. Cannot fetch case details.');
          return of(null);
        }
        return runInInjectionContext(this.injector, () => {
          return this.getPatientDetailsFromFirestore(user.uid, patientKey).pipe(
            map(data => data as Case || null) // Cast to Case type
          );
        });
      })
    );
  }

  getPatientDetailsFromFirestore(userId: string, patientKey: string): Observable<any | null> {
    return runInInjectionContext(this.injector, () => {
      const docRef = doc(this.firestore, PathConstants.getPatientCaseDocumentPath(userId, patientKey));
      return from(getDoc(docRef)).pipe(
        map((snapshot: DocumentSnapshot) => {
          if (snapshot.exists()) {
            console.log('[CaseService] Patient details found in Firestore:', snapshot.data());
            return snapshot.data();
          } else {
            console.log('[CaseService] Patient details not found in Firestore.');
            return null;
          }
        }),
        catchError(error => {
          console.error('[CaseService] Error fetching patient details from Firestore:', error);
          return of(null);
        })
      );
    });
  }

  getPatientDetails(providerOrgCode: string, patientId: string, userId: string, forceCloudFunctionCall: boolean = false): Observable<any> {
    console.log('[CaseService] getPatientDetails called with:', { providerOrgCode, patientId, userId, forceCloudFunctionCall });

    if (forceCloudFunctionCall) {
      console.log('[CaseService] Forcing Cloud Function call.');
      return this.callCloudFunctionAndSave(providerOrgCode, patientId, userId);
    } else {
      return this.getPatientDetailsFromFirestore(userId, patientId).pipe(
        switchMap(firestoreData => {
          if (firestoreData) {
            console.log('[CaseService] Returning patient details from Firestore:', firestoreData);
            this.cases$.next(firestoreData); // Update the BehaviorSubject
            return of(firestoreData);
          } else {
            console.log('[CaseService] Patient details not in Firestore, calling Cloud Function.');
            return this.callCloudFunctionAndSave(providerOrgCode, patientId, userId);
          }
        })
      );
    }
  }

  private callCloudFunctionAndSave(providerOrgCode: string, patientId: string, userId: string): Observable<any> {
    return runInInjectionContext(this.injector, () => {
      const callable = httpsCallable(this.functions, 'getPatientDetails');
      const payload = { providerOrgCode, patientId };
      console.log('[CaseService] Sending payload to Cloud Function:', payload);
      return from(callable(payload)).pipe(
        switchMap((response: any) => {
          console.log('[CaseService] Cloud Function raw response:', response);
          const patientData = response.data;
          console.log('[CaseService] Cloud Function patientData:', patientData);
          if (patientData) {
            if (patientData.ct == null || patientData.ct == undefined) { patientData.ct = false; }
            console.log('[CaseService] Updating caseDetailsSubject$ with patientData.');
            this.cases$.next(patientData); // Update the BehaviorSubject
            return this.savePatientDataToFirestore(userId, patientId, patientData).pipe(
              switchMap(() => {
                if (patientData.filestore) {
                  return this.savePatientFilesToFirestore(userId, patientId, patientData.filestore).pipe(
                    map(() => patientData)
                  );
                } else {
                  return of(patientData);
                }
              })
            );
          } else {
            console.warn('[CaseService] No patient data returned from Cloud Function. Emitting null.');
            return of(null);
          }
        }),
        catchError(error => {
          console.error('[CaseService] Error calling Cloud Function:', error);
          return throwError(() => error);
        })
      );
    });
  }

  savePatientDataToFirestore(userId: string, patientKey: string, patientData: any): Observable<void> {
    return runInInjectionContext(this.injector, () => {
      const { filestore, ...patientDetails } = patientData;
      const cleanedData = this.cleanDataForFirestore(patientDetails);
      const docRef = doc(this.firestore, PathConstants.getPatientCaseDocumentPath(userId, patientKey));
      console.log('[CaseService] Saving patient data to Firestore:', { path: PathConstants.getPatientCaseDocumentPath(userId, patientKey), patientData: cleanedData });
      return from(setDoc(docRef, cleanedData, { merge: true })).pipe(
        tap(() => console.log('[CaseService] Patient data saved successfully.')),
        catchError(error => {
          console.error('[CaseService] Error saving patient data to Firestore:', error);
          return throwError(() => error);
        })
      );
    });
  }

  private cleanDataForFirestore(data: any): any {
    // Firestore does not accept `undefined` values. This will recursively remove them.
    if (data === null || data === undefined) {
      return null;
    }
    if (typeof data !== 'object') {
      return data;
    }
    if (Array.isArray(data)) {
      return data.map(item => this.cleanDataForFirestore(item));
    }
    const newObj: { [key: string]: any } = {};
    for (const key in data) {
      if (data.hasOwnProperty(key) && data[key] !== undefined) {
        const value = this.cleanDataForFirestore(data[key]);
        if (value !== undefined) {
          newObj[key] = value;
        }
      }
    }
    return newObj;
  }

  savePatientFilesToFirestore(userId: string, patientKey: string, filestoreData: any): Observable<void> {
    return runInInjectionContext(this.injector, () => {
      const fileStoreCollectionRef = collection(this.firestore, PathConstants.getPatientDocuments(userId, patientKey));
      const batch = writeBatch(this.firestore);

      console.log('[CaseService] Saving patient files to Firestore:', { path: PathConstants.getPatientDocuments(userId, patientKey), filestoreData });

      return from(getDocs(fileStoreCollectionRef)).pipe(
        switchMap(existingDocsSnapshot => {
          const existingFiles: Record<string, any> = {};
          existingDocsSnapshot.forEach(doc => {
            existingFiles[doc.id] = doc.data();
          });

          const updatedFileIds = new Set(Object.keys(filestoreData || {}));

          // Add or update changed files
          Object.entries(filestoreData || {}).forEach(([fileId, fileData]) => {
            const existingFileData = existingFiles[fileId];
            if (!existingFileData || JSON.stringify(existingFileData) !== JSON.stringify(fileData)) {
              const fileDocRef = doc(this.firestore, PathConstants.getPatientDocuments(userId, patientKey), fileId);
              batch.set(fileDocRef, fileData);
              console.log(`[CaseService] Batching set for file: ${fileId}`);
            }
          });

          // Mark deleted:true for files that are not in the new filestore
          Object.keys(existingFiles).forEach((fileId) => {
            if (!updatedFileIds.has(fileId)) {
              const fileDocRef = doc(this.firestore, PathConstants.getPatientDocuments(userId, patientKey), fileId);
              batch.set(fileDocRef, { ...existingFiles[fileId], deleted: true }, { merge: true });
              console.log(`[CaseService] Batching soft delete for file: ${fileId}`);
            }
          });

          return from(batch.commit()).pipe(
            tap(() => console.log('[CaseService] Patient files batch committed successfully.')),
            catchError(error => {
              console.error('[CaseService] Error committing patient files batch:', error);
              return throwError(() => error);
            })
          );
        }),
        catchError(error => {
          console.error('[CaseService] Error processing patient files for batching:', error);
          return throwError(() => error);
        })
      );
    });
  }
}
// Trivial change to force recompile
